import { Blend } from './Blend';
import { BlendResource } from './BlendResource';
import { BlendState } from './BlendState';
import { Matrix3 } from './Matrix3';
import { Rect } from './Rect';
import { RenderDevice } from './RenderDevice';
import { SceneObject } from './SceneObject';
import { SpriteBatch } from './SpriteBatch';
import { Texture } from './Texture';
import { Vector2 } from './Vector2';

export class ExternalSceneObject extends SceneObject {
  private canvas: HTMLCanvasElement;
  private texture: Texture;
  private renderDevice: RenderDevice;

  constructor(
    canvas: HTMLCanvasElement,
    renderDevice: RenderDevice,
    blendState: BlendState | null = null
  ) {
    super();
    this.canvas = canvas;
    this.renderDevice = renderDevice;
    // No mipmaps: updateImpl re-uploads the canvas every frame and
    // updateTextureFromCanvas does not regenerate the mip chain
    this.texture = renderDevice.createTextureFromCanvas(canvas, false)!;

    // Configure blend state for proper transparency
    this.blend = new BlendResource('premultipliedAlpha');

    if (!blendState) {
      this.blend.construct(
        new BlendState(
          true,
          Blend.One, // Source blend factor
          Blend.InvSrcAlpha // Destination blend factor
        )
      );
    } else {
      this.blend.construct(blendState);
    }
  }

  updateImpl(dt: number): void {
    this.renderDevice.updateTextureFromCanvas(this.texture, this.canvas);
  }

  withSourceBlend(blend: Blend): ExternalSceneObject {
    this.blend.construct(new BlendState(this.blend.data!.enabled, blend, this.blend.data!.dst));
    return this;
  }

  withDestinationBlend(blend: Blend): ExternalSceneObject {
    this.blend.construct(new BlendState(this.blend.data!.enabled, this.blend.data!.src, blend));
    return this;
  }

  drawImpl(spriteBatch: SpriteBatch, transform: Matrix3): void {
    const rect = new Rect(Vector2.Zero, new Vector2(this.texture.width, this.texture.height));
    spriteBatch.drawRect(this.texture, rect, rect, transform);
  }
}
